import { Box, Card, CardContent, Chip, Grid, Skeleton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense, memo } from 'react';
import { BorderedWrapper, SelectedWrapper } from './style';

import FilterSearch from '@Components/Filter/Search';
import SbImage from '@Components/Image';
import HorizontalTrack from '@Components/Tracking/HorizontalTracking';
import { useDispatch, useSelector } from 'react-redux';
import { SHIPMENT_STATUS } from '../../../constants/Shipment';
import { AppDispatch, RootStateType } from '../../../store';
import { BoxType } from '../../../store/reducers/Shipment/Type';
import { trackShipment } from '../../../store/reducers/Tracking/actions';
import { ScrollableBox } from '../../CreateShipment/styles';
import ViewAction from '../../ViewSingleShipment/ViewAction';
import SingleBox from './SingleBox';

import SbButton from '@Components/Button';
import { CSP_TYPES } from '@Constants/Shipment';
import { editFinalForward } from '@Reducers/AdminShipment';
import { ExpandLessRounded } from '@mui/icons-material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import Image from '@Components/Image';
import { getFedExDetails, getVendorsAccount } from '@Reducers/AdminShipment/actions';
import { getVendorSettingList } from '@Reducers/Shipment/actions';
import { getCreditLimit, getOutstandingBalanceForPostpaid } from '@Reducers/Credit';
import { userLogin } from '@Services/hasAdminAccess';
import { getOutstandingBalanceInvoiceForPostpaid } from '@Reducers/Credit/actions';
import { AddressType } from '@Reducers/GetRates/Type';
import { getChargeableWeight } from '@Reducers/GetRates/actions';
import { TrackShipmentDataType } from '@Reducers/Tracking/type';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import {
  AWB_PREFILL_CSPS,
  fetchAwbPrefill,
  mapPrefillToFedEx,
  mapPrefillToDhl,
  mapPrefillToAramex,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/services/awbPrefillService';

// ─── Lazy-loaded heavy carrier components (code-split, loaded only when needed) ───
const VerticalTracking = lazy(() => import('@Components/Tracking/VerticalTracking'));
const UserFedXInformation = lazy(() => import('./UserFedX/UserFedXInformation'));
const UserAramexInformation = lazy(() => import('./UserAramex/userAramexInformation'));
const UserDHLInformation = lazy(() => import('./UserDHL/UserDHLInformation'));
const UserBluedart = lazy(() => import('./UserBluedart/UserBluedart'));
const UserUniversal = lazy(() => import('./UserUniversal/UserUniversal'));
const UserThinkLogistics = lazy(() => import('./UserIThinkLogistics/UserThinkLogistics'));
const UserIThinkLogisticsDomestic = lazy(() => import('./UserIThinkLogisticsDomestic/UserIThinkLogisticsDomestic'));
const UserLinex = lazy(() => import('./UserLinex/UserLinex'));
const UserDelhivery = lazy(() => import('./UserDelhivery/UserDelhivery'));
const UserNandan = lazy(() => import('./UserNandan/UserNandan'));
const UserTrackon = lazy(() => import('./UserTrackon/UserTrackon'));
const UserDpduk = lazy(() => import('./UserDpduk/UserDpduk'));
const UserUps = lazy(() => import('./UserUps/UserUps'));

// ─── Pure static data — defined once, never recreated ───────────────────────
const SearchFilterList = [
  { label: 'Carrier AWB', value: 'carrierNumber' },
  { label: 'Shipment Number', value: 'shipmentNumber' },
];

const SUPPORTED_CSP_CODES = new Set([
  CSP_TYPES.fedex.value,
  CSP_TYPES.ithinklogistics.value,
  CSP_TYPES.ithink_logistics_domestic.value,
  CSP_TYPES.linex.value,
  CSP_TYPES.delhivery.value,
  CSP_TYPES.trackon.value,
  CSP_TYPES.dpduk.value,
  CSP_TYPES.movin_ups.value,
  CSP_TYPES.shree_nandan_courier_limited.value,
  CSP_TYPES.dhlIndia.value,
  'cirro',
  CSP_TYPES.dhl.value,
  CSP_TYPES.bluedart.value,
  CSP_TYPES.universal.value,
  CSP_TYPES.stage.value,
  CSP_TYPES.aramex.value,
]);

function getAddressObj({ pincode, city, country, state }: AddressType) {
  return { city, state, country, pincode };
}

// ─── Fallback skeleton shown while lazy components load ─────────────────────
const CarrierSkeleton = memo(() => (
  <Grid item xs={12}>
    <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
  </Grid>
));

// ─── Tracking panel skeleton shown on first load ────────────────────────────
const TrackingPanelSkeleton = memo(() => (
  <Grid item lg={8} xl={9} md={8} sm={12} xs={12}>
    <SelectedWrapper sx={{ p: 2 }}>
      <BorderedWrapper sx={{ py: 3, px: 2.5, width: '100%' }}>
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
      </BorderedWrapper>
    </SelectedWrapper>
  </Grid>
));

// ─── Memoized box list — only re-renders when filteredBoxes/selectedBox changes ──
// ✅ Fix the interface — use the correct type instead of unknown
const BoxList = memo(
  ({
    filteredBoxes,
    selectedBoxId,
    setSelectedBox,
    trackingDataByShipmentId,
  }: {
    filteredBoxes: BoxType[];
    selectedBoxId: string | undefined;
    setSelectedBox: (box: BoxType) => void;
    trackingDataByShipmentId: Record<string, TrackShipmentDataType> | null; // ✅ was Record<string, unknown>
  }) => (
    <>
      {filteredBoxes.map((box: BoxType, i: number) => (
        <SingleBox
          key={box.childAWBNumber || `tracking-box-${i}`}
          box={{ ...box, id: i.toString() }}
          setSelectedBox={setSelectedBox}
          childAWBNumber={box.childAWBNumber || ''}
          carrierNumber={box.carrierNumber || ''}
          trackingData={
            box.childAWBNumber && trackingDataByShipmentId ? trackingDataByShipmentId[box.childAWBNumber] : null
          }
          isStatus={box?.status}
          isSelected={selectedBoxId === i.toString()}
        />
      ))}
    </>
  ),
);

// ─── Main component ──────────────────────────────────────────────────────────
function TrackingDetails() {
  const dispatch = useDispatch<AppDispatch>();
  const isUserLogin = userLogin();
  const theme = useTheme();

  const [viewMore, setViewMore] = useState<boolean>(true);
  const [selectedBox, setSelectedBox] = useState<BoxType>();
  const [filterSelectValue, setFilterSelectValue] = useState('carrierNumber');
  const [filterText, setFilterText] = useState('');
  const [filteredBoxes, setFilteredBoxes] = useState<BoxType[]>([]);
  const [showAWB, setShowAWB] = useState<string>('');
  const [hasVendorSettings, setHasVendorSettings] = useState(false);
  const [prefilledFedEx, setPrefilledFedEx] = useState<any>({});
  const [prefilledDhl, setPrefilledDhl] = useState<any>({});
  const [prefilledAramex, setPrefilledAramex] = useState<any>({});
  const [isPrefillLoading, setIsPrefillLoading] = useState<boolean>(false);
  const prefillFetchedRef = useRef<{ [key: string]: boolean }>({});

  // ─── Selectors ──────────────────────────────────────────────────────────
  const carrierIntegration = useSelector((state: RootStateType) => state.carrierIntegration.carriers);
  const { shipmentById, shipmentConnectOnWarehouse } = useSelector((state: RootStateType) => state.shipment);
  const {
    stageDetails,
    fedExDetails,
    bluedartDetails,
    aramexDetails,
    dhlDetails,
    universalDetails,
    thinkLogisticsDetails,
    iThinkLogisticsDomesticDetails,
    linexDetails,
    delhiveryDetails,
    nandanDetails,
    trackonDetails,
    dpdukDetails,
    upsDetails,
  } = useSelector((state: RootStateType) => state.adminShipment);
  const { creditLimit, totalInvoiceAmtPaid, totalShipmentAmt, totalShipmentAmtPaid } = useSelector(
    (state: RootStateType) => state.Credit,
  );
  const { token } = useSelector((state: RootStateType) => state.loginUser);
  const shipmentpayment = useSelector((state: RootStateType) => state.adminShipment.shipmentpayment);
  const isUserHasCarrierAccess = useSelector((state: RootStateType) => state.loginUser?.profile);
  const isFinalForwardEditable = useSelector((state: RootStateType) => state.adminShipment.editFinalForward);
  const { data: trackingData, trackingDataByShipmentId } = useSelector((state: RootStateType) => state.trackShipment);
  const organizationName = useSelector((state: RootStateType) => state.orgSetup?.organizationData);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);

  const { name = '' } = organizationData || {};
  const statusDetails = trackingData?.statusDetails || [];
  const boxes = shipmentById?._packageId?.box || [];

  // ─── Memoized derived values ─────────────────────────────────────────────
  const { lastTrackingStatus, lastTrackingMessage, lastTrackingLocation } = useMemo(() => {
    if (!statusDetails.length) return { lastTrackingStatus: '', lastTrackingMessage: '', lastTrackingLocation: '' };
    return {
      lastTrackingStatus: SHIPMENT_STATUS[statusDetails[0].status as keyof typeof SHIPMENT_STATUS]?.label,
      lastTrackingMessage: statusDetails[0].msg,
      lastTrackingLocation: statusDetails[0].location,
    };
  }, [statusDetails]);

  const outStandingBalanceResult = useMemo(
    () => totalShipmentAmt - totalShipmentAmtPaid - totalInvoiceAmtPaid,
    [totalShipmentAmt, totalShipmentAmtPaid, totalInvoiceAmtPaid],
  );
  const updateAvailableLimit = useMemo(
    () => creditLimit - outStandingBalanceResult,
    [creditLimit, outStandingBalanceResult],
  );
  const connectionType = useMemo(
    () => (shipmentConnectOnWarehouse?.length ? 'W-D' : 'D-D'),
    [shipmentConnectOnWarehouse?.length],
  );
  const isEditable = useMemo(
    () => (shipmentById?.isFinalForward ? isFinalForwardEditable : true),
    [shipmentById?.isFinalForward, isFinalForwardEditable],
  );
  const btnText = isFinalForwardEditable ? 'Update' : 'Forward';

  const hasAnyCarrierDetails = useMemo(
    () =>
      [
        stageDetails,
        fedExDetails,
        bluedartDetails,
        aramexDetails,
        dhlDetails,
        universalDetails,
        thinkLogisticsDetails,
        iThinkLogisticsDomesticDetails,
        linexDetails,
        delhiveryDetails,
        trackonDetails,
        dpdukDetails,
        upsDetails,
      ].some(
        details =>
          details && typeof details === 'object' && Object.keys(details).length > 0 && '_id' in details && details._id,
      ),
    [
      stageDetails,
      fedExDetails,
      bluedartDetails,
      aramexDetails,
      dhlDetails,
      universalDetails,
      thinkLogisticsDetails,
      iThinkLogisticsDomesticDetails,
      linexDetails,
      delhiveryDetails,
      trackonDetails,
      dpdukDetails,
      upsDetails,
    ],
  );

  const isGenerateAWBDisabledPrepaid = useMemo(
    () =>
      !isUserHasCarrierAccess?.isHybridUser ||
      hasVendorSettings ||
      outStandingBalanceResult > 0 ||
      (shipmentById?.status !== 'forwarded' &&
        shipmentById?._accountId?.isPrepaid &&
        Number(shipmentpayment?.payableAmount) > 0),
    [isUserHasCarrierAccess, hasVendorSettings, shipmentById, shipmentpayment, outStandingBalanceResult],
  );
  const isGenerateAWBDisabledPostpaid = useMemo(
    () =>
      !isUserHasCarrierAccess?.isHybridUser ||
      ((organizationName?.name === 'Speedbox UAT' ||
        organizationName?.name === 'Allied Express Pvt. Ltd.' ||
        organizationName?.name === 'Speedbox Preprod') &&
        (hasVendorSettings ||
          (!shipmentById?.isFinalForward &&
            (shipmentById?.status === 'at_warehouse' ||
              shipmentById?.status === 'assigned' ||
              shipmentById?.status === 'placed') &&
            !shipmentById?._accountId?.isPrepaid &&
            updateAvailableLimit < 0))),
    [isUserHasCarrierAccess, organizationName, hasVendorSettings, shipmentById, updateAvailableLimit],
  );

  // ─── Ref guards — prevent duplicate API calls ────────────────────────────
  const chargeableWeightFetchedRef = useRef<string | null>(null);

  // ─── getChargeableWeight: moved from SingleBoxView → called ONCE here ────
  useEffect(() => {
    const shipmentNumber = shipmentById?.shipmentNumber;
    if (!shipmentNumber || chargeableWeightFetchedRef.current === shipmentNumber) return;
    if (!shipmentById?._packageId?.box?.length) return;

    chargeableWeightFetchedRef.current = shipmentNumber;
    const selectedUnit = shipmentById._packageId.unitOfMeasurement.split('-')[0];

    dispatch(
      getChargeableWeight({
        destination: { ...getAddressObj(shipmentById._consigneeId) },
        source: { ...getAddressObj(shipmentById._shipperId) },
        package: {
          box: shipmentById._packageId.box.map(b => ({
            weight: b.weight.toString(),
            length: Number(b.length),
            width: Number(b.width),
            height: Number(b.height),
          })),
          unitOfMeasurement: selectedUnit,
        },
        document: shipmentById._packageId?.isDocument,
        _courierId: shipmentById.sellingPricing?._courierId,
        shipmentEdit: true,
        inbound: shipmentById.sellingPricing?.inbound,
        outbound: shipmentById.sellingPricing?.outbound,
        shipmentNumber,
      }),
    );
  }, [shipmentById?.shipmentNumber]);

  useEffect(() => {
    if (!boxes?.length) return;
    const newBoxCopy: BoxType[] = boxes.map(box => ({ ...box, selected: !!box.selected }));
    setFilteredBoxes(newBoxCopy);
    setSelectedBox({ ...newBoxCopy[0] });
  }, [boxes]);

  useEffect(() => {
    if (selectedBox?.childAWBNumber) dispatch(trackShipment(selectedBox.childAWBNumber));
  }, [selectedBox]);

  useEffect(() => {
    const shipmentNumber = shipmentById?.shipmentNumber;
    const cspCode = shipmentById?.buyingPricing?.code;
    if (!shipmentNumber || !cspCode || !SUPPORTED_CSP_CODES.has(cspCode)) return;

    dispatch(getVendorsAccount(cspCode));
    if (carrierIntegration.length > 0) dispatch(getFedExDetails({ shipmentNumber, type: 'D-D', csp: cspCode }));
    if (isUserLogin) dispatch(getFedExDetails({ shipmentNumber, type: connectionType, csp: cspCode }));
  }, [
    shipmentById?.shipmentNumber,
    shipmentConnectOnWarehouse?.length,
    shipmentById?.buyingPricing?.code,
    isUserLogin,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (shipmentById?.buyingPricing?.code && token?.accountNumber) {
        try {
          const data = await dispatch(
            getVendorSettingList({ csp: shipmentById.buyingPricing.code, accountNumber: token.accountNumber }),
          ).unwrap();
          setHasVendorSettings(data?.data === null);
        } catch (e) {
          console.error('Vendor settings error:', e);
          setHasVendorSettings(false);
        }
      }
      try {
        await Promise.all([
          dispatch(getCreditLimit()).unwrap(),
          dispatch(getOutstandingBalanceForPostpaid()).unwrap(),
          dispatch(getOutstandingBalanceInvoiceForPostpaid()).unwrap(),
        ]);
      } catch (e) {
        console.error('Credit fetch error:', e);
      }
    };
    fetchData();
  }, [shipmentById?.buyingPricing?.code, token?.accountNumber]);

  // Reset prefill cache on shipment change
  useEffect(() => {
    prefillFetchedRef.current = {};
    setPrefilledFedEx({});
    setPrefilledDhl({});
    setPrefilledAramex({});
  }, [shipmentById?.shipmentNumber]);

  // Fetch AWB prefill data when showAWB is fedex, dhl, or aramex and no carrier details exist
  useEffect(() => {
    const shipmentNumber = shipmentById?.shipmentNumber;

    if (!showAWB || !shipmentNumber || !(AWB_PREFILL_CSPS as readonly string[]).includes(showAWB)) {
      return;
    }

    if (hasAnyCarrierDetails) {
      return;
    }

    const fetchKey = `${shipmentNumber}_${showAWB}`;
    if (prefillFetchedRef.current[fetchKey]) {
      return;
    }

    let isMounted = true;
    const loadPrefill = async () => {
      setIsPrefillLoading(true);
      try {
        const data = await fetchAwbPrefill(shipmentNumber, showAWB);
        if (data && isMounted) {
          prefillFetchedRef.current[fetchKey] = true;
          if (showAWB === 'fedex') {
            setPrefilledFedEx(mapPrefillToFedEx(data));
          } else if (showAWB === 'dhl' || showAWB === 'dhl_india') {
            setPrefilledDhl(mapPrefillToDhl(data));
          } else if (showAWB === 'aramex') {
            setPrefilledAramex(mapPrefillToAramex(data));
          }
        }
      } catch (err) {
        console.error('Failed to load user AWB prefill data:', err);
      } finally {
        if (isMounted) {
          setIsPrefillLoading(false);
        }
      }
    };

    loadPrefill();

    return () => {
      isMounted = false;
    };
  }, [showAWB, shipmentById?.shipmentNumber, hasAnyCarrierDetails]);

  // ─── Stable callbacks ────────────────────────────────────────────────────
  const handleForward = useCallback(() => {
    if (!shipmentById?.shipmentNumber) return;
    // dispatch(
    //   updateShipment({
    //     carrierDetails: { awbShowToUser: true },
    //     shipmentNumber: shipmentById.shipmentNumber,
    //     isFinalForward: true,
    //   }),
    // );
    dispatch(editFinalForward(false));
    setShowAWB('');
  }, [shipmentById?.shipmentNumber]);

  const handleClose = useCallback(() => {
    dispatch(editFinalForward(false));
    setShowAWB('');
  }, []);

  const handleAWBToggle = useCallback(
    () => setShowAWB(prev => (prev ? '' : shipmentById?.buyingPricing?.code || 'HI')),
    [shipmentById?.buyingPricing?.code],
  );

  const getFilteredBoxDetails = useCallback(() => {
    const temp =
      filterText && filterSelectValue === 'carrierNumber'
        ? shipmentById?._packageId?.box.filter(i => i?.carrierNumber?.toLowerCase().includes(filterText))
        : filterText && filterSelectValue === 'shipmentNumber'
        ? shipmentById?._packageId?.box.filter(i => i?.childAWBNumber?.includes(filterText))
        : boxes;
    if (temp) setFilteredBoxes(temp);
  }, [filterText, filterSelectValue, shipmentById?._packageId?.box, boxes]);

  const handleTextClear = useCallback(() => {
    setFilterText('');
    setFilteredBoxes(boxes);
  }, [boxes]);

  // ─── Shared props for all carrier User* components ───────────────────────
  const carrierCommonProps = useMemo(
    () => ({ onClickForward: handleForward, onClose: handleClose, isEditable, btnText }),
    [handleForward, handleClose, isEditable, btnText],
  );

  const handleDownloadCarrierLabel = useCallback(() => {
    if (shipmentById?.shipmentNumber) {
      dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
    }
  }, [shipmentById?.shipmentNumber]);

  return (
    <Card
      id="tracking_detail"
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TimelineOutlinedIcon />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>Tracking Details</Typography>
            {lastTrackingStatus && (
              <Chip
                label={lastTrackingStatus}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              />
            )}
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
            Carrier milestones, box-level tracking, and event history
          </Typography>
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'common.black' }}>
            {lastTrackingStatus}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{lastTrackingMessage}</Typography>
        </Box>

        <Grid container>
          <HorizontalTrack data={trackingData?.statusArray || []} />
          {lastTrackingLocation && statusDetails?.length > 1 && (
            <Typography className="w-full flex sb-text-gray" justifyContent="flex-end">
              {lastTrackingLocation}
            </Typography>
          )}
        </Grid>

        <Grid container alignItems="baseline">
          <Grid item xs={6}>
            {shipmentById?.sellingPricing?.serviceImage && (
              <SbImage src={shipmentById.sellingPricing.serviceImage} alt="logo" height={70} width={150} />
            )}
            {shipmentById?.sellingPricing?.serviceName && (
              <Typography className="md-text" sx={{ color: '#7D878C' }}>
                {shipmentById.sellingPricing.serviceName}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6} textAlign="end">
            <ViewAction setViewMore={setViewMore} viewMore={viewMore} />
          </Grid>
        </Grid>

        {viewMore && (
          <Grid container className="my-3" spacing={2}>
            {/* ── Left panel: box list + AWB button ── */}
            <Grid item lg={4} xl={3} md={4} sm={12} xs={12}>
              <FilterSearch
                onSelectChange={setFilterSelectValue}
                list={SearchFilterList}
                selectValue={filterSelectValue}
                textValue={filterText}
                onTextChange={e => setFilterText(e.target.value)}
                onTextSearch={getFilteredBoxDetails}
                onTextClear={handleTextClear}
              />
              <ScrollableBox container id="fadedScroll" sx={{ pr: { xs: 1, md: 2 }, mt: 2 }} rowGap={2}>
                {/* ✅ Memoized list — won't re-render unless boxes/selection changes */}
                <BoxList
                  filteredBoxes={filteredBoxes}
                  selectedBoxId={selectedBox?.id}
                  setSelectedBox={setSelectedBox}
                  trackingDataByShipmentId={trackingDataByShipmentId}
                />
              </ScrollableBox>

              {/* ── Prepaid AWB button ── */}
              {shipmentById?._accountId?.isPrepaid && (
                <Tooltip title={isGenerateAWBDisabledPrepaid ? 'Payment for this shipment is outstanding' : ''} arrow>
                  <span>
                    <SbButton
                      containerClass="py-1 px-3 normal-case mt-3"
                      variant="outlined"
                      startIcon={<Image src="/images/icons/GenerateAwb.svg" alt="Edit" />}
                      endIcon={showAWB ? <ExpandLessRounded /> : <GridExpandMoreIcon />}
                      onClick={handleAWBToggle}
                      disabled={isGenerateAWBDisabledPrepaid}
                    >
                      Generate AWB
                    </SbButton>
                  </span>
                </Tooltip>
              )}

              {/* ── Postpaid AWB button ── */}
              {name !== 'Cargo Solutions Network' && !shipmentById?._accountId?.isPrepaid && (
                <Tooltip
                  title={
                    isGenerateAWBDisabledPostpaid
                      ? 'The account has crossed its credit limit. Ensure your credit limit is adjusted before processing the shipment!'
                      : ''
                  }
                  arrow
                >
                  <span>
                    <SbButton
                      containerClass="py-1 px-3 normal-case mt-3"
                      variant="outlined"
                      startIcon={<Image src="/images/icons/GenerateAwb.svg" alt="Edit" />}
                      endIcon={showAWB ? <ExpandLessRounded /> : <GridExpandMoreIcon />}
                      onClick={handleAWBToggle}
                      disabled={isGenerateAWBDisabledPostpaid}
                    >
                      Generate AWB
                    </SbButton>
                  </span>
                </Tooltip>
              )}
            </Grid>

            {/* ── Right panel: vertical tracking (lazy) ── */}
            <Suspense fallback={<TrackingPanelSkeleton />}>
              {trackingData?.reverseDateWise ? (
                <Grid item lg={8} xl={9} md={8} sm={12} xs={12}>
                  <SelectedWrapper sx={{ p: 2 }}>
                    <BorderedWrapper sx={{ py: 3, px: 2.5, width: '100%', overflowX: 'auto' }}>
                      <VerticalTracking
                        data={trackingData.reverseDateWise}
                        rtoShipmentNumber={shipmentById?.rtoShipmentNumber || ''}
                      />
                    </BorderedWrapper>
                  </SelectedWrapper>
                </Grid>
              ) : (
                <TrackingPanelSkeleton />
              )}
            </Suspense>

            {/* ── Carrier AWB panels (lazy — only the active one loads its JS chunk) ── */}
            {isPrefillLoading ? (
              <CarrierSkeleton />
            ) : (
              <Suspense fallback={showAWB ? <CarrierSkeleton /> : null}>
                {showAWB === CSP_TYPES.fedex.value && (
                  <UserFedXInformation
                    {...carrierCommonProps}
                    fedExDetails={{
                      ...(hasAnyCarrierDetails ? fedExDetails : { ...fedExDetails, ...prefilledFedEx }),
                      type: connectionType,
                    }}
                    showFedExDetails
                  />
                )}
                {(showAWB === CSP_TYPES.dhlIndia.value || showAWB === CSP_TYPES.dhl.value) && (
                  <UserDHLInformation
                    {...carrierCommonProps}
                    initialStates={{
                      ...(hasAnyCarrierDetails ? dhlDetails : { ...dhlDetails, ...prefilledDhl }),
                      type: connectionType,
                    }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.aramex.value && (
                  <UserAramexInformation
                    {...carrierCommonProps}
                    initialStates={{
                      ...(hasAnyCarrierDetails ? aramexDetails : { ...aramexDetails, ...prefilledAramex }),
                      type: connectionType,
                    }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.bluedart.value && (
                  <UserBluedart
                    {...carrierCommonProps}
                    initialStates={{ ...bluedartDetails, type: connectionType }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.universal.value && (
                  <UserUniversal
                    {...carrierCommonProps}
                    initialStates={{ ...universalDetails, type: connectionType }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.ithinklogistics.value && (
                  <UserThinkLogistics
                    {...carrierCommonProps}
                    initialStates={{ ...thinkLogisticsDetails, type: connectionType }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.ithink_logistics_domestic.value && (
                  <UserIThinkLogisticsDomestic
                    {...carrierCommonProps}
                    initialStates={{ ...iThinkLogisticsDomesticDetails, type: connectionType }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.linex.value && (
                  <UserLinex
                    {...carrierCommonProps}
                    initialStates={{ ...linexDetails, type: connectionType }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.delhivery.value && (
                  <UserDelhivery
                    {...carrierCommonProps}
                    initialStates={{ ...delhiveryDetails, type: connectionType }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.shree_nandan_courier_limited.value && (
                  <UserNandan
                    {...carrierCommonProps}
                    initialStates={{ ...nandanDetails, type: connectionType }}
                    showFedExDetails
                  />
                )}
                {showAWB === CSP_TYPES.trackon.value && (
                  <UserTrackon
                    {...carrierCommonProps}
                    initialStates={{ ...trackonDetails, type: connectionType }}
                    showTrackonDetails
                  />
                )}
                {showAWB === CSP_TYPES.dpduk.value && (
                  <UserDpduk
                    {...carrierCommonProps}
                    initialStates={{ ...dpdukDetails, type: connectionType }}
                    showDpdukDetails
                  />
                )}
                {showAWB === CSP_TYPES.movin_ups.value && (
                  <UserUps
                    {...carrierCommonProps}
                    initialStates={{ ...upsDetails, type: connectionType }}
                    showUpsDetails
                  />
                )}
              </Suspense>
            )}
          </Grid>
        )}
        {hasAnyCarrierDetails && (
          <Box
            display="flex"
            border="1px solid"
            className="border-grey font-medium md-text p-2"
            borderRadius={1}
            alignItems="center"
            width="fit-content"
          >
            This shipment has been forwarded.
            {shipmentById?.buyingPricing?._courierId === shipmentById?.sellingPricing?._courierId && (
              <Box
                component="span"
                className="font-medium md-text ml-1"
                sx={{
                  color: '#485fc7',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={handleDownloadCarrierLabel}
              >
                Download the carrier AWB
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default memo(TrackingDetails);
